import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { MapPinIcon } from '@heroicons/react/24/outline';

/**
 * Reusable list page content component with pull-to-refresh and infinite scroll
 * 
 * @param {Object} props
 * @param {Array} props.projects - Array of projects to display
 * @param {boolean} props.isLoading - Loading state
 * @param {boolean} props.isFetchingNextPage - Fetching next page state
 * @param {boolean} props.hasNextPage - Whether there are more pages
 * @param {Function} props.fetchNextPage - Function to fetch next page
 * @param {Function} props.refetch - Function to refetch data
 * @param {Object} props.error - Error object
 * @param {Function} props.renderProject - Function to render each project: (project, index) => ReactNode
 * @param {boolean} props.isMobile - Whether on mobile device
 * @param {boolean} props.isDesktopSplitView - Whether in desktop split view (affects grid columns)
 * @param {boolean} props.isFullyExpanded - Whether mobile sheet is fully expanded
 * @param {Function} props.onRefresh - Callback when pull-to-refresh completes
 * @param {Array} props.invalidateKeys - Query keys to invalidate on refresh
 * @param {Object} props.emptyState - Empty state config { title, message, icon }
 * @param {Object} props.gridConfig - Grid column config { mobile, tablet, desktop, splitView }
 * @param {React.ReactNode} props.header - Optional header content
 * @param {React.ReactNode} props.beforeContent - Optional content to render before header/list (e.g., hero section)
 * @param {boolean} props.showLoadMoreButton - Show "Load More" button (default: true, hidden on mobile)
 */
const ListPageContent = ({
  projects = [],
  isLoading = false,
  isFetchingNextPage = false,
  hasNextPage = false,
  fetchNextPage,
  refetch,
  error = null,
  renderProject,
  isMobile = false,
  isDesktopSplitView = false,
  isFullyExpanded = false,
  onRefresh,
  invalidateKeys = [],
  emptyState = {
    title: 'No results found',
    message: 'Try adjusting your search or filters',
    icon: MapPinIcon,
  },
  gridConfig = {
    mobile: 2,
    tablet: 2,
    desktop: 4,
    xl: 5,
    '2xl': 6,
    splitView: { desktop: 2, xl: 3 },
  },
  header,
  beforeContent,
  showLoadMoreButton = true,
}) => {
  // Normalize list to prevent accidental undefined states during refetches
  const normalizedProjects = Array.isArray(projects) ? projects : [];

  // Pull-to-refresh state
  const [isPullToRefresh, setIsPullToRefresh] = useState(false);
  const [pullDistance, setPullDistance] = useState(0);
  const [startY, setStartY] = useState(0);
  const [canPull, setCanPull] = useState(false);
  const scrollContainerRef = useRef(null);

  // Intersection observer for infinite scroll
  const observerTarget = useRef(null);
  const observedNodeRef = useRef(null);
  const hasNextPageRef = useRef(hasNextPage);
  const fetchNextPageRef = useRef(fetchNextPage);
  const isFetchingNextPageRef = useRef(isFetchingNextPage);

  // Keep refs in sync
  useEffect(() => {
    hasNextPageRef.current = hasNextPage;
    fetchNextPageRef.current = fetchNextPage;
    isFetchingNextPageRef.current = isFetchingNextPage;
  }, [hasNextPage, fetchNextPage, isFetchingNextPage]);

  // Create intersection observer once
  useEffect(() => {
    if (!observerTarget.current) {
      observerTarget.current = new IntersectionObserver(
        (entries) => {
          if (entries[0].isIntersecting && hasNextPageRef.current && !isFetchingNextPageRef.current) {
            fetchNextPageRef.current();
          }
        },
        { threshold: 0.1 }
      );
    }

    return () => {
      if (observerTarget.current) {
        observerTarget.current.disconnect();
        observerTarget.current = null;
        observedNodeRef.current = null;
      }
    };
  }, []);

  // Stable ref callback for observing last project
  const observeLastProject = useCallback(
    (node) => {
      if (!observerTarget.current) return;
      
      // Unobserve previous node if it exists
      if (observedNodeRef.current && observedNodeRef.current !== node) {
        observerTarget.current.unobserve(observedNodeRef.current);
      }
      
      // Observe new node
      if (node) {
        observerTarget.current.observe(node);
        observedNodeRef.current = node;
      } else {
        observedNodeRef.current = null;
      }
    },
    []
  );

  // Pull-to-refresh handlers
  const handleTouchStart = useCallback(
    (e) => {
      if (!isMobile || !scrollContainerRef.current) return;

      const scrollContainer = scrollContainerRef.current;
      const isAtTop = scrollContainer.scrollTop <= 0;
      setCanPull(isAtTop);

      if (isAtTop) {
        setStartY(e.touches[0].clientY);
      }
    },
    [isMobile]
  );

  const handleTouchMove = useCallback(
    (e) => {
      if (!canPull || isPullToRefresh || !startY) return;

      const currentY = e.touches[0].clientY;
      const distance = currentY - startY;

      if (distance > 0) {
        e.preventDefault();
        const maxPull = 80;
        const resistance = 0.5;
        const adjustedDistance = Math.min(distance * resistance, maxPull);
        setPullDistance(adjustedDistance);
      }
    },
    [canPull, isPullToRefresh, startY]
  );

  const handleTouchEnd = useCallback(async () => {
    if (!canPull) {
      setPullDistance(0);
      setStartY(0);
      setCanPull(false);
      return;
    }

    const threshold = 60;

    if (pullDistance >= threshold && !isPullToRefresh) {
      setIsPullToRefresh(true);

      try {
        // Call onRefresh if provided, otherwise use refetch
        if (onRefresh) {
          await onRefresh(invalidateKeys);
        } else if (refetch) {
          await refetch(invalidateKeys);
        }

        setTimeout(() => {
          setIsPullToRefresh(false);
          setPullDistance(0);
          setStartY(0);
          setCanPull(false);
        }, 1000);
      } catch (error) {
        console.error('Pull-to-refresh failed:', error);
        setIsPullToRefresh(false);
        setPullDistance(0);
        setStartY(0);
        setCanPull(false);
      }
    } else {
      setPullDistance(0);
      setStartY(0);
      setCanPull(false);
    }
  }, [canPull, pullDistance, isPullToRefresh, onRefresh, refetch, invalidateKeys]);

  // Add pull-to-refresh event listeners
  useEffect(() => {
    const scrollContainer = scrollContainerRef.current;
    if (!scrollContainer || !isMobile) return;

    const handleTouchStartNonPassive = (e) => handleTouchStart(e);
    const handleTouchMoveNonPassive = (e) => handleTouchMove(e);
    const handleTouchEndNonPassive = (e) => handleTouchEnd(e);

    scrollContainer.addEventListener('touchstart', handleTouchStartNonPassive, { passive: false });
    scrollContainer.addEventListener('touchmove', handleTouchMoveNonPassive, { passive: false });
    scrollContainer.addEventListener('touchend', handleTouchEndNonPassive, { passive: false });

    return () => {
      scrollContainer.removeEventListener('touchstart', handleTouchStartNonPassive);
      scrollContainer.removeEventListener('touchmove', handleTouchMoveNonPassive);
      scrollContainer.removeEventListener('touchend', handleTouchEndNonPassive);
    };
  }, [isMobile, handleTouchStart, handleTouchMove, handleTouchEnd]);

  // Reset pull-to-refresh when view changes
  useEffect(() => {
    setPullDistance(0);
    setStartY(0);
    setCanPull(false);
    setIsPullToRefresh(false);
  }, [isFullyExpanded]);

  // Build grid classes - use explicit Tailwind classes
  const gridClasses = useMemo(() => {
    if (isDesktopSplitView) {
      // Split view: 2 cols mobile, 2 tablet, 2 desktop, 3 xl
      return 'grid gap-3 grid-cols-2 md:grid-cols-2 lg:grid-cols-2 xl:grid-cols-3';
    }
    // Full view: 2 cols mobile, 2 tablet, 4 desktop, 5 xl, 6 2xl
    return 'grid gap-3 grid-cols-2 md:grid-cols-2 lg:grid-cols-4 xl:grid-cols-5 2xl:grid-cols-6';
  }, [isDesktopSplitView]);

  const EmptyIcon = emptyState.icon || MapPinIcon;
  const hasProjects = normalizedProjects.length > 0;
  const showInitialLoadingState = isLoading && !hasProjects;
  const showSkeleton = showInitialLoadingState || isPullToRefresh;

  return (
    <div
      ref={scrollContainerRef}
      className="relative h-full overflow-y-auto mobile-scroll px-5"
      style={{
        transform: `translateY(${pullDistance}px)`,
        transition: isPullToRefresh ? 'transform 0.3s ease' : 'none',
        paddingBottom: isMobile ? `calc(env(safe-area-inset-bottom, 0px) + 120px)` : undefined,
      }}
    >
      {/* Before content (e.g., hero section) */}
      {beforeContent && (
        <div className="flex-shrink-0">
          {beforeContent}
        </div>
      )}

      {/* Header */}
      {header && (
        <div
          className={`${
            isMobile
              ? isFullyExpanded
                ? 'py-2 space-y-4'
                : 'pt-3 pb-2 space-y-4'
              : 'pb-4 sm:pb-4 sm:pt-4 sticky bg-white dark:bg-black top-0 z-[10000]'
          }`}
        >
          {header}
        </div>
      )}

      {/* Pull-to-refresh indicator */}
      {(pullDistance > 0 || isPullToRefresh) && (
        <div
          className="absolute top-0 left-0 right-0 flex items-center justify-center z-20 bg-gradient-to-b from-white/98 to-white/90 dark:from-gray-900/98 dark:to-gray-900/90 border-b border-gray-200/30 dark:border-gray-700/30"
          style={{
            height: `${Math.max(pullDistance, isPullToRefresh ? 60 : 0)}px`,
            transform: `translateY(-${Math.max(pullDistance, isPullToRefresh ? 60 : 0)}px)`,
          }}
        >
          <div className="flex items-center justify-center text-gray-700 dark:text-gray-300">
            <div className={`${isPullToRefresh ? 'animate-spin' : ''} transition-all duration-300`}>
              {isPullToRefresh ? (
                <div className="w-8 h-8 border-2 border-green-500 border-t-transparent rounded-full animate-spin"></div>
              ) : pullDistance >= 60 ? (
                <div className="text-green-500 text-2xl animate-bounce">↓</div>
              ) : (
                <div className="text-gray-400 text-2xl">↓</div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Content */}
      <div className="py-6 pt-2 pb-32">
        {showSkeleton ? (
          <div className={gridClasses}>
            {[...Array(8)].map((_, index) => (
              <div key={index} className="animate-pulse">
                <div className="aspect-[20/19] bg-gray-200 dark:bg-gray-700 rounded-xl"></div>
                <div className="pt-3 space-y-2">
                  <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-3/4"></div>
                  <div className="flex items-center gap-2">
                    <div className="w-5 h-5 bg-gray-200 dark:bg-gray-700 rounded-full"></div>
                    <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-20"></div>
                  </div>
                  <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-1/3"></div>
                </div>
              </div>
            ))}
          </div>
        ) : error ? (
          <div className="text-center py-12">
            <div className="text-red-600 dark:text-red-400 mb-4">
              {error.message || 'Failed to load data'}
            </div>
            <button
              onClick={() => refetch && refetch(invalidateKeys)}
              className={`bg-blue-600 hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-600 text-white rounded-md transition-colors mobile-button no-tap-highlight touch-feedback ${
                isMobile ? 'px-6 py-3' : 'px-4 py-2'
              }`}
            >
              Try Again
            </button>
          </div>
        ) : !hasProjects ? (
          <div className="text-center py-12">
            <EmptyIcon className="w-12 h-12 text-gray-400 dark:text-gray-500 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
              {emptyState.title}
            </h3>
            <p className="text-gray-600 dark:text-gray-400">{emptyState.message}</p>
          </div>
        ) : (
          <>
            <div className={gridClasses}>
              {normalizedProjects.map((project, index) => {
                const isNearEnd = index >= normalizedProjects.length - 3;
                return (
                  <div key={project.id} ref={isNearEnd ? observeLastProject : null}>
                    {renderProject(project, index)}
                  </div>
                );
              })}
            </div>
            {isFetchingNextPage && (
              <div className="flex justify-center items-center py-8">
                <div className="flex items-center space-x-2">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600 dark:border-blue-400"></div>
                  <span className="text-gray-600 dark:text-gray-400 text-sm">Loading more results...</span>
                </div>
              </div>
            )}
            {showLoadMoreButton && hasNextPage && !isFetchingNextPage && normalizedProjects.length >= 20 && (
              <div className="text-center mt-8">
                <button
                  onClick={() => fetchNextPage()}
                  className="bg-blue-600 hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-600 text-white rounded-lg transition-colors font-medium mobile-button no-tap-highlight touch-feedback px-6 py-3"
                >
                  Load More Results
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default ListPageContent;

